import { gql } from '@apollo/client'
import {
  BookmarkActiveIcon,
  BookmarkIcon,
  Confirm,
  DeleteIcon,
  Dropdown,
  FileSearchIcon,
  FlagIcon,
  IconButton,
  Link,
  MenuItem,
  MoreVerticalIcon,
  PrintIcon,
  useToast,
} from '@farol-ds/react'
import {
  CopyContentButton,
  DocSave,
  DocSearchElement,
  FEATURE_CODES,
  toolbarStyles,
  useDeleteDocument,
  useMetrics,
  useSaveDocument,
  useToolbar,
} from '@jusbrasil-web/docview/core'
import { LoginTrigger } from '@jusbrasil-web/shared-bifrost'
import { useBoxModal } from '@jusbrasil-web/shared-box-lock'
import { FEATURE_NAME } from '@jusbrasil-web/shared-ga4-events'
import { RefObject, useState } from 'react'

import { ConvertMotionButton } from '../convert-motion-button'
import {
  MotionToolbar_DocumentFragment,
  MotionToolbar_ViewerFragment,
} from './__generated__/motion-toolbar.graphql'
import { REPORT_MOTION_URL } from './constants'
import styles from './motion-toolbar.module.scss'

export interface MotionToolbarProps {
  document: MotionToolbar_DocumentFragment
  viewer: MotionToolbar_ViewerFragment | null
  docSearchRef: RefObject<DocSearchElement>
  docContentRef: RefObject<HTMLDivElement>
  ga4ComponentClass: string
  onCopyContent?: (successfulAttempt: boolean) => void
  onDocumentDownload?: (successfulAttempt: boolean) => void
}

const boxProps = {
  featureCode: FEATURE_CODES.COPY_MODELOS_CONTENT,
  fromComponent: 'docview/MotionToolbar',
}

const IMAGE_PLACEHOLDER_TEXT_PATTERN =
  /imagem não disponível[\s\S]+?preservar a parte do processo\.\s*/gi

export function MotionToolbar(props: MotionToolbarProps) {
  const {
    document,
    viewer,
    docSearchRef,
    docContentRef,
    ga4ComponentClass,
    onCopyContent,
    onDocumentDownload,
  } = props
  const { artifact, docId, canCopyContent, viewerSaved } = document

  const { ref: dispatcherRef, sendMetrics, sendCopyEvents } = useMetrics<HTMLDivElement>()
  const { setOpen } = useBoxModal({
    appId: 'web-docview',
    boxOptions: {},
    pageMetadata: {},
    viewer,
    enabled: !canCopyContent,
    ...boxProps,
  })
  const toast = useToast()
  const deleteDocument = useDeleteDocument({
    artifact,
    document,
    docId,
  })
  const { toggleSave } = useSaveDocument({
    document,
    viewer,
    ga4EventData: { componentClass: ga4ComponentClass },
    sendMetrics,
  })
  const [isDeleteConfirmOpened, setDeleteConfirmOpened] = useState(false)
  const { onSearchButtonClick, onPrint, onDelete } = useToolbar({
    document,
    docSearchRef,
    toast,
    deleteDocument,
    setDeleteConfirmOpened,
    sendMetrics,
    featureName: FEATURE_NAME.MOTION_DOCUMENT_SEARCH,
    componentClass: ga4ComponentClass,
  })

  const isAuthor = !!document.publisher?.isViewer
  const isAdmin = !!viewer?.user?.isAdmin
  const hasPaidPlan = !!viewer?.hasPaidPlan
  const reportMotionUrl = `${REPORT_MOTION_URL}?source=${document.url}&pid=${viewer?.pid}`

  const onCopyContentButtonClick = () => {
    sendCopyEvents()
    onCopyContent?.(true)
  }

  const onReportClick = () => {
    sendMetrics('Doc.ReportClicked')
  }

  const shouldAllowCopy = () => {
    if (!viewer) {
      onCopyContent?.(false)
      return false
    }

    const hasPendingPayment = !!viewer.subscriptions?.some((s) => s?.overdue)
    if (hasPendingPayment || !canCopyContent) {
      setOpen(true)
      sendMetrics('Peca.SawCopyContentLock')
      onCopyContent?.(false)
      return false
    }

    return true
  }

  const disableActions = !document.viewerCanAccessFullDocument

  return (
    <div className={toolbarStyles.toolbar} ref={dispatcherRef}>
      <ConvertMotionButton
        document={document}
        viewer={viewer}
        disabled={disableActions}
        onClick={onDocumentDownload}
      />

      <LoginTrigger
        isLogged={!!viewer}
        options={{
          eventData: {
            feature_code: boxProps.featureCode,
            from_component: boxProps.fromComponent,
          },
        }}
      >
        <CopyContentButton
          contentRef={docContentRef}
          onClick={onCopyContentButtonClick}
          shouldAllowCopy={shouldAllowCopy}
          removePatterns={[IMAGE_PLACEHOLDER_TEXT_PATTERN]}
          className={styles.copyBtn}
          disabled={disableActions}
        >
          Copiar<span data-btn-fragment> peça</span>
        </CopyContentButton>
      </LoginTrigger>

      <div className={toolbarStyles.toolbarIconBtns}>
        {hasPaidPlan && !disableActions && (
          <IconButton
            icon={<FileSearchIcon />}
            onClick={onSearchButtonClick}
            data-testid="search-btn"
            label="Buscar no documento"
          />
        )}

        <div className={toolbarStyles.toolbarSaveButton}>
          <DocSave
            viewer={viewer}
            document={document}
            ga4EventData={{ componentClass: ga4ComponentClass }}
          />
        </div>

        <Dropdown>
          <Dropdown.Trigger>
            <IconButton icon={<MoreVerticalIcon />} label="Mais" />
          </Dropdown.Trigger>

          <Dropdown.Menu
            title="Mais opções"
            /* no autofocus after interaction with the dropdown (then close), better UX since the items open modals */
            onCloseAutoFocus={(evt) => evt.preventDefault()}
          >
            <LoginTrigger isLogged={!!viewer}>
              <Dropdown.MenuItem onClick={toggleSave} className={toolbarStyles.saveMenuItem}>
                <MenuItem>
                  <MenuItem.Content>
                    <MenuItem.Leading>
                      {viewerSaved ? <BookmarkActiveIcon /> : <BookmarkIcon />}
                    </MenuItem.Leading>
                    Salvar
                  </MenuItem.Content>
                </MenuItem>
              </Dropdown.MenuItem>
            </LoginTrigger>
            <Dropdown.MenuItem onClick={onPrint}>
              <MenuItem>
                <MenuItem.Content>
                  <MenuItem.Leading>
                    <PrintIcon />
                  </MenuItem.Leading>
                  Imprimir
                </MenuItem.Content>
              </MenuItem>
            </Dropdown.MenuItem>
            <Dropdown.MenuItem>
              <MenuItem>
                <Link
                  type="ghost"
                  href={reportMotionUrl}
                  target="_blank"
                  rel="noopener"
                  data-testid="toolbar-document-report-item"
                  onClick={onReportClick}
                >
                  <MenuItem.Content>
                    <MenuItem.Leading>
                      <FlagIcon />
                    </MenuItem.Leading>
                    Reportar
                  </MenuItem.Content>
                </Link>
              </MenuItem>
            </Dropdown.MenuItem>
            {(isAdmin || isAuthor) && (
              <Dropdown.MenuItem onClick={() => setDeleteConfirmOpened(true)}>
                <MenuItem type="danger">
                  <MenuItem.Content>
                    <MenuItem.Leading>
                      <DeleteIcon />
                    </MenuItem.Leading>
                    Deletar
                  </MenuItem.Content>
                </MenuItem>
              </Dropdown.MenuItem>
            )}
          </Dropdown.Menu>
        </Dropdown>

        {(isAdmin || isAuthor) && (
          <Confirm
            title="Deseja realmente deletar este documento?"
            confirmLabel="OK"
            confirmProps={{
              loading: deleteDocument.loading,
            }}
            danger
            open={isDeleteConfirmOpened}
            onConfirm={onDelete}
            onCancel={() => setDeleteConfirmOpened(false)}
          />
        )}
      </div>
    </div>
  )
}

MotionToolbar.fragments = {
  document: gql`
    fragment MotionToolbar_document on Document {
      canCopyContent: viewerCanUseFeature(featureCode: "copy_pecas_content")
      docId
      artifact
      publisher {
        isViewer
      }
      viewerSaved
      url
      viewerCanAccessFullDocument

      ...UseSaveDocument_document
      ...DocSave_document
      ...ConvertMotionButton_document
    }

    ${useSaveDocument.fragments.document}
    ${DocSave.fragments.document}
    ${ConvertMotionButton.fragments.document}
  `,

  viewer: gql`
    fragment MotionToolbar_viewer on Profile {
      subscriptions {
        overdue
      }
      hasPaidPlan
      user {
        isAdmin
      }

      ...UseSaveDocument_viewer
      ...DocSave_viewer
      ...ConvertMotionButton_viewer
      ...useBoxModal_viewer
    }

    ${useSaveDocument.fragments.viewer}
    ${DocSave.fragments.viewer}
    ${ConvertMotionButton.fragments.viewer}
    ${useBoxModal.fragments.viewer}
  `,
}
